import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { User, UserRole } from '../types/user';
import { 
  hasRole, 
  hasAnyRole, 
  getPrimaryRole, 
  getDashboardRoute, 
  shouldRedirectAfterLogin 
} from '../utils/permissions';
import { toast } from '../components/ui/sonner';

interface AuthResponse {
  message: string;
  user: User;
  access_token: string;
  token_type: string;
}

// Plan types for subscription management
export type PlanType = 'free' | 'basic' | 'premium';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  userPlan: PlanType;
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (name: string, email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
  isEmailVerified: () => boolean;
  resendVerificationEmail: () => Promise<void>;
  checkSubscriptionStatus: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  isImpersonating: () => boolean;
  impersonatedUser: User | null;
  // Role-based methods
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  getPrimaryRole: () => UserRole;
  getDashboardRoute: () => string;
  shouldRedirectAfterLogin: (currentPath: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState<PlanType>('free');

  // Function to check subscription status
  const checkSubscriptionStatus = async () => {
    try {
      if (!user) return;
      
      const planFromStorage = localStorage.getItem('user_plan') as PlanType;
      if (planFromStorage && ['free', 'basic', 'premium'].includes(planFromStorage)) {
        setUserPlan(planFromStorage);
      }
      
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      if (authService.isAuthenticated()) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);

          const impersonating = localStorage.getItem('isImpersonating') === 'true';
          if(impersonating) {
            const adminUserData = localStorage.getItem('admin_user');
            if(adminUserData) {
              setImpersonatedUser(JSON.parse(adminUserData));
            }
          }
          await checkSubscriptionStatus();
        } catch (error) {
          console.error('Auth check failed:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      await checkSubscriptionStatus();
      toast("Login successful");
      return response;
    } catch (error: any) {
      const errorMessage = error.message || "Login failed. Please check your credentials.";
      toast(errorMessage, {
        className: "bg-red-50 text-red-600 border-red-200",
      });
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response = await authService.register(name, email, password);
      setUser(response.user);
      setUserPlan('free');
      localStorage.setItem('user_plan', 'free');
      toast("Registration successful! Please check your email to verify your account.");
      return response;
    } catch (error: any) {
      const errorMessage = error.message || "Registration failed. Please try again.";
      toast(errorMessage, {
        className: "bg-red-50 text-red-600 border-red-200",
      });
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setImpersonatedUser(null);
    setUserPlan('free');
  };

  const isAuthenticated = () => {
    return authService.isAuthenticated();
  };

  const isEmailVerified = () => {
    return user ? authService.isEmailVerified(user) : false;
  };

  const resendVerificationEmail = async () => {
    await authService.resendVerificationEmail();
    toast("Verification email sent! Please check your inbox.");
  };

  const isImpersonating = () => {
    return localStorage.getItem('isImpersonating') === 'true';
  }

  const impersonate = async (userId: string) => {
    if (!user) throw new Error("Admin user not found");

    localStorage.setItem('admin_token', localStorage.getItem('access_token') || '');
    localStorage.setItem('admin_user', JSON.stringify(user));
    localStorage.setItem('isImpersonating', 'true');

    try {
      const response = await authService.impersonate(userId);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      window.location.href = '/dashboard';
    } catch(error) {
      // Clear impersonation storage on failure
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('isImpersonating');
      throw error;
    }
  }

  const stopImpersonating = async () => {
    try {
      await authService.stopImpersonating();
    } catch (error) {
      console.error("Backend failed to stop impersonating, proceeding with frontend cleanup", error);
    } finally {
      const adminToken = localStorage.getItem('admin_token');
      const adminUser = localStorage.getItem('admin_user');
      
      if (adminToken && adminUser) {
        localStorage.setItem('access_token', adminToken);
        localStorage.setItem('user', adminUser);

        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('isImpersonating');

        window.location.href = '/admin';
      } else {
        // If something is wrong, just log out
        await logout();
        window.location.href = '/login';
      }
    }
  }

  // Role-based methods
  const hasUserRole = (role: UserRole) => hasRole(user, role);
  const hasUserAnyRole = (roles: UserRole[]) => hasAnyRole(user, roles);
  const getUserPrimaryRole = () => getPrimaryRole(user);
  const getUserDashboardRoute = () => getDashboardRoute(user);
  const shouldUserRedirectAfterLogin = (currentPath: string) => shouldRedirectAfterLogin(user, currentPath);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      userPlan,
      login, 
      register, 
      logout, 
      isAuthenticated,
      isEmailVerified,
      resendVerificationEmail,
      checkSubscriptionStatus,
      impersonate,
      stopImpersonating,
      isImpersonating,
      impersonatedUser,
      // Role-based methods
      hasRole: hasUserRole,
      hasAnyRole: hasUserAnyRole,
      getPrimaryRole: getUserPrimaryRole,
      getDashboardRoute: getUserDashboardRoute,
      shouldRedirectAfterLogin: shouldUserRedirectAfterLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
